import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import SignatureCanvas from "react-signature-canvas";
import "./horizonTable.css";
import "../forms/themeCheckbox.css";
import formLogo from "../../assets/logo2 2.png";
import { generateFatigueManagementPlanPdf } from "../../utils/fatigueManagementPlanPdf.js";

const THEME = "#8b1634";
const WORD_BORDER = "#000000";
const WORD_HEADER_BG = "rgba(139, 22, 52, 0.08)";
const WORD_TITLE = THEME;

const DOC = {
  table: {
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "fixed",
    fontSize: 11,
    fontFamily: "Arial, Helvetica, sans-serif",
    color: "#333",
  },
  th: {
    background: WORD_HEADER_BG,
    border: `1px solid ${WORD_BORDER}`,
    fontWeight: 700,
    fontSize: 10,
    padding: "6px 8px",
    verticalAlign: "top",
    textAlign: "left",
    color: "#000",
  },
  thCtr: {
    background: WORD_HEADER_BG,
    border: `1px solid ${WORD_BORDER}`,
    fontWeight: 700,
    fontSize: 10,
    padding: "6px 8px",
    verticalAlign: "middle",
    textAlign: "center",
    color: "#000",
  },
  td: {
    border: `1px solid ${WORD_BORDER}`,
    padding: "6px 8px",
    verticalAlign: "top",
    background: "#fff",
  },
  tdGrey: {
    border: `1px solid ${WORD_BORDER}`,
    padding: "8px 10px",
    verticalAlign: "top",
    background: "rgba(139, 22, 52, 0.06)",
    fontSize: 10,
    lineHeight: 1.45,
    color: "#1a1a1a",
  },
  sectionBar: {
    background: WORD_HEADER_BG,
    border: `1px solid ${WORD_BORDER}`,
    fontWeight: 700,
    fontSize: 10,
    padding: "6px 8px",
    textAlign: "left",
    color: "#000",
  },
  input: {
    width: "100%",
    border: "none",
    outline: "none",
    fontSize: 11,
    padding: "4px 2px",
    background: "transparent",
    boxSizing: "border-box",
  },
  textarea: {
    width: "100%",
    border: "none",
    outline: "none",
    fontSize: 11,
    padding: "6px 4px",
    minHeight: 72,
    resize: "vertical",
    background: "transparent",
    boxSizing: "border-box",
  },
};

const PLAN_ALERT_OPTIONS = [
  {
    left: { value: "Extremely Alert", label: "Extremely Alert" },
    right: { value: "Some signs of Sleepiness", label: "Some signs of Sleepiness" },
  },
  {
    left: { value: "Very Alert", label: "Very Alert" },
    right: { value: "Sleepy but no efforts to keep awake", label: "Sleepy but no efforts to keep awake" },
  },
  {
    left: { value: "Alert", label: "Alert" },
    right: { value: "Sleepy, Some effort to keep awake", label: "Sleepy, Some effort to keep awake" },
  },
  {
    left: { value: "Rather Alert", label: "Rather Alert" },
    right: { value: "Very sleepy, great effort keeping awake", label: "Very sleepy, great effort keeping awake" },
  },
  {
    left: { value: "Neither Alert nor Sleepy", label: "Neither Alert nor Sleepy" },
    right: null,
  },
];

const EMPTY_FORM = {
  userId: "",
  employeeName: "",
  employeeNumber: "",
  sentinelNumber: "",
  normalWorkLocation: "",
  jobRole: "",
  date: "",
  managerNameAndRole: "",
  reasonForCreation: "",
  howAlertAreYouFeeling: "",
  workStatusOverPlan: "",
  existingWorkPatterns: { agreedActions: "", reviewPeriodDate: "" },
  reviewOfControlMeasures: { agreedActions: "", reviewPeriodDate: "" },
  medicalReviewPlans: { agreedActions: "", reviewPeriodDate: "" },
  otherComments: { agreedActions: "", reviewPeriodDate: "" },
  actionPlanAgreement: {
    employeeSignature: "",
    employeeDate: "",
    managerSignature: "",
    managerDate: "",
  },
};

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL;
  return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
}

function formatDateYYYYMMDDLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseYYYYMMDDLocal(ymd) {
  if (!ymd || typeof ymd !== "string") return null;
  const parts = ymd.split("-");
  if (parts.length !== 3) return null;
  const y = Number(parts[0]);
  const mo = Number(parts[1]);
  const day = Number(parts[2]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(day)) return null;
  return new Date(y, mo - 1, day);
}

function formatDate(d) {
  if (!d) return "—";
  const date = new Date(d);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function getDateInputValue(d) {
  if (!d) return "";
  if (typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  if (typeof d === "string" && d.includes("/")) {
    const [dd, mm, yyyy] = d.split("/");
    if (dd && mm && yyyy) return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
  }
  const dt = new Date(d);
  if (!Number.isNaN(dt.getTime())) return formatDateYYYYMMDDLocal(dt);
  return "";
}

function drawSignatureOnCanvas(sigRef, signatureValue) {
  if (!sigRef?.current) return;
  sigRef.current.clear();
  if (!signatureValue) return;
  let sigSrc = String(signatureValue);
  if (!sigSrc.startsWith("data:image")) {
    sigSrc = `data:image/png;base64,${sigSrc}`;
  }
  const img = new window.Image();
  img.onload = () => {
    if (!sigRef.current) return;
    const canvas = sigRef.current.getCanvas();
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const canvasAspect = canvas.width / canvas.height;
    const imgAspect = img.width / img.height;
    let drawWidth;
    let drawHeight;
    let drawX;
    let drawY;
    if (imgAspect > canvasAspect) {
      drawWidth = canvas.width;
      drawHeight = canvas.width / imgAspect;
      drawX = 0;
      drawY = (canvas.height - drawHeight) / 2;
    } else {
      drawHeight = canvas.height;
      drawWidth = canvas.height * imgAspect;
      drawX = (canvas.width - drawWidth) / 2;
      drawY = 0;
    }
    ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
  };
  img.onerror = () => {};
  img.src = sigSrc;
}

const SIG_CANVAS_H = 140;

function FatigueSignaturePad({ canvasRef, onEnd, onClear, isOpen }) {
  const wrapRef = useRef(null);
  const [dims, setDims] = useState(null);

  useLayoutEffect(() => {
    if (!isOpen) {
      setDims(null);
      return undefined;
    }
    const measure = () => {
      const el = wrapRef.current;
      if (!el) return;
      const raw = el.getBoundingClientRect().width;
      const w = Math.max(180, Math.floor(raw));
      if (w >= 180) {
        setDims((prev) => prev || { w, h: SIG_CANVAS_H });
      }
    };
    measure();
    const t1 = setTimeout(measure, 0);
    const t2 = setTimeout(measure, 80);
    const t3 = setTimeout(measure, 240);
    const t4 = setTimeout(measure, 480);
    const t5 = setTimeout(() => {
      setDims((prev) => {
        if (prev) return prev;
        const el = wrapRef.current;
        const raw = el ? el.getBoundingClientRect().width : 320;
        const w = Math.max(180, Math.floor(raw || 320));
        return { w, h: SIG_CANVAS_H };
      });
    }, 700);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      clearTimeout(t5);
    };
  }, [isOpen]);

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
          gap: 8,
        }}
      >
        <span style={{ fontSize: 12, color: THEME, fontWeight: 700 }}>Sign in the box below</span>
        <button
          type="button"
          className="btn btn-sm"
          style={{
            background: THEME,
            color: "#fff",
            border: `1px solid ${THEME}`,
            borderRadius: 8,
            padding: "6px 14px",
            fontSize: 12,
            fontWeight: 600,
          }}
          onClick={onClear}
        >
          Clear
        </button>
      </div>
      <div
        ref={wrapRef}
        style={{
          width: "100%",
          minHeight: SIG_CANVAS_H + 2,
          overflow: "hidden",
          position: "relative",
          borderRadius: 10,
          border: `1px solid ${THEME}`,
          background: "#ffffff",
          boxShadow: "0 2px 10px rgba(139, 22, 52, 0.08)",
          touchAction: "none",
        }}
      >
        {dims ? (
          <SignatureCanvas
            ref={canvasRef}
            penColor={THEME}
            backgroundColor="#ffffff"
            clearOnResize={false}
            velocityFilterWeight={0.7}
            minWidth={1}
            maxWidth={2.5}
            dotSize={1.5}
            throttle={16}
            canvasProps={{
              width: dims.w,
              height: dims.h,
              style: {
                width: `${dims.w}px`,
                height: `${dims.h}px`,
                display: "block",
                touchAction: "none",
                border: "none",
                borderRadius: 10,
                background: "#ffffff",
                boxSizing: "border-box",
              },
            }}
            onEnd={onEnd}
          />
        ) : (
          <div style={{ height: SIG_CANVAS_H, background: "rgba(139, 22, 52, 0.03)" }} />
        )}
      </div>
    </div>
  );
}

function DFatigueManagementPlanPage() {
  const [forms, setForms] = useState([]);
  const [appUsers, setAppUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [payload, setPayload] = useState(EMPTY_FORM);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteForm, setDeleteForm] = useState(null);
  const [workerRequiredError, setWorkerRequiredError] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState("");
  const pageSize = 10;
  const employeeSigRef = useRef(null);
  const managerSigRef = useRef(null);

  useEffect(() => {
    fetchForms();
    fetchAppUsers();
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (user && user.role) setCurrentUserRole(user.role);
    } catch {
      setCurrentUserRole("");
    }
  }, []);

  async function fetchAppUsers() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/app-users", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setAppUsers(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setAppUsers([]);
    }
  }

  useEffect(() => {
    if (!showModal || !editingForm) return;
    const t = setTimeout(() => {
      drawSignatureOnCanvas(employeeSigRef, editingForm.actionPlanAgreement?.employeeSignature);
      drawSignatureOnCanvas(managerSigRef, editingForm.actionPlanAgreement?.managerSignature);
    }, 850);
    return () => clearTimeout(t);
  }, [showModal, editingForm?._id]);

  async function fetchForms() {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/d-fatigue-management-plan", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setForms(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setForms([]);
    } finally {
      setLoading(false);
    }
  }

  function openAddModal() {
    setEditingForm(null);
    setWorkerRequiredError(false);
    setPayload({ ...EMPTY_FORM });
    setShowModal(true);
    setTimeout(() => {
      employeeSigRef.current?.clear();
      managerSigRef.current?.clear();
    }, 900);
  }

  function openEditModal(form) {
    setEditingForm(form);
    setWorkerRequiredError(false);
    setPayload({
      userId: form.userId?._id || form.userId || "",
      employeeName: form.employeeName || "",
      employeeNumber: form.employeeNumber || "",
      sentinelNumber: form.sentinelNumber || "",
      normalWorkLocation: form.normalWorkLocation || "",
      jobRole: form.jobRole || "",
      date: getDateInputValue(form.date),
      managerNameAndRole: form.managerNameAndRole || "",
      reasonForCreation: form.reasonForCreation || "",
      howAlertAreYouFeeling: form.howAlertAreYouFeeling || "",
      workStatusOverPlan: form.workStatusOverPlan || "",
      existingWorkPatterns: {
        agreedActions: form.existingWorkPatterns?.agreedActions || "",
        reviewPeriodDate: getDateInputValue(form.existingWorkPatterns?.reviewPeriodDate),
      },
      reviewOfControlMeasures: {
        agreedActions: form.reviewOfControlMeasures?.agreedActions || "",
        reviewPeriodDate: getDateInputValue(form.reviewOfControlMeasures?.reviewPeriodDate),
      },
      medicalReviewPlans: {
        agreedActions: form.medicalReviewPlans?.agreedActions || "",
        reviewPeriodDate: getDateInputValue(form.medicalReviewPlans?.reviewPeriodDate),
      },
      otherComments: {
        agreedActions: form.otherComments?.agreedActions || "",
        reviewPeriodDate: getDateInputValue(form.otherComments?.reviewPeriodDate),
      },
      actionPlanAgreement: {
        employeeSignature: form.actionPlanAgreement?.employeeSignature || "",
        employeeDate: getDateInputValue(form.actionPlanAgreement?.employeeDate),
        managerSignature: form.actionPlanAgreement?.managerSignature || "",
        managerDate: getDateInputValue(form.actionPlanAgreement?.managerDate),
      },
    });
    setShowModal(true);
  }

  function saveEmployeeSignatureToPayload() {
    if (employeeSigRef.current && !employeeSigRef.current.isEmpty()) {
      const sig = employeeSigRef.current.getTrimmedCanvas().toDataURL("image/png");
      setPayload((prev) => ({
        ...prev,
        actionPlanAgreement: { ...(prev.actionPlanAgreement || {}), employeeSignature: sig },
      }));
    }
  }

  function saveManagerSignatureToPayload() {
    if (managerSigRef.current && !managerSigRef.current.isEmpty()) {
      const sig = managerSigRef.current.getTrimmedCanvas().toDataURL("image/png");
      setPayload((prev) => ({
        ...prev,
        actionPlanAgreement: { ...(prev.actionPlanAgreement || {}), managerSignature: sig },
      }));
    }
  }

  function clearEmployeeSignature() {
    employeeSigRef.current?.clear();
    setPayload((prev) => ({
      ...prev,
      actionPlanAgreement: { ...(prev.actionPlanAgreement || {}), employeeSignature: "" },
    }));
  }

  function clearManagerSignature() {
    managerSigRef.current?.clear();
    setPayload((prev) => ({
      ...prev,
      actionPlanAgreement: { ...(prev.actionPlanAgreement || {}), managerSignature: "" },
    }));
  }

  async function handleSave() {
    const uid = payload.userId != null ? String(payload.userId).trim() : "";
    if (!uid) {
      setWorkerRequiredError(true);
      toast.error("Worker is required.");
      return;
    }
    setWorkerRequiredError(false);
    const actionPlanAgreement = { ...(payload.actionPlanAgreement || {}) };
    if (employeeSigRef.current && !employeeSigRef.current.isEmpty()) {
      actionPlanAgreement.employeeSignature = employeeSigRef.current.getTrimmedCanvas().toDataURL("image/png");
    }
    if (managerSigRef.current && !managerSigRef.current.isEmpty()) {
      actionPlanAgreement.managerSignature = managerSigRef.current.getTrimmedCanvas().toDataURL("image/png");
    }
    const body = { ...payload, actionPlanAgreement };
    if (!editingForm) {
      delete body.id;
      body.status = "approved"; // new plan is created as Approved (manager-created, no approval needed)
    }
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const url = editingForm
        ? `${getBaseUrl()}/api/d-fatigue-management-plan/${editingForm._id}`
        : `${getBaseUrl()}/api/d-fatigue-management-plan`;
      const method = editingForm ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(editingForm ? "Fatigue management plan updated." : "Fatigue management plan created.");
        setShowModal(false);
        setEditingForm(null);
        setPayload({ ...EMPTY_FORM });
        fetchForms();
        window.dispatchEvent(new Event("exceedanceFormsUpdated"));
      } else {
        toast.error(data.message || "Failed to save.");
      }
    } catch {
      toast.error("Failed to save.");
    } finally {
      setSaving(false);
    }
  }

  async function handleGeneratePdf(record) {
    try {
      await generateFatigueManagementPlanPdf(record);
      toast.success("SF-008-D Fatigue Management Plan PDF generated successfully!");
    } catch {
      toast.error("Failed to generate PDF.");
    }
  }

  async function handleDeleteConfirm() {
    if (!deleteForm?._id) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${getBaseUrl()}/api/d-fatigue-management-plan/${deleteForm._id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success("Fatigue management plan deleted.");
        setShowDeleteModal(false);
        setDeleteForm(null);
        fetchForms();
        window.dispatchEvent(new Event("exceedanceFormsUpdated"));
      } else {
        toast.error(data.message || "Failed to delete.");
      }
    } catch {
      toast.error("Failed to delete.");
    }
  }

  const filtered = useMemo(() => {
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return forms.filter((f) => {
      const u = f.userId && typeof f.userId === "object" ? f.userId : null;
      const userStr = u ? [u.firstName, u.lastName].filter(Boolean).join(" ").trim() || u.email || "" : "";
      const fields = [
        f.id,
        f.employeeName,
        f.employeeNumber,
        f.sentinelNumber,
        f.normalWorkLocation,
        f.jobRole,
        f.managerNameAndRole,
        f.reasonForCreation,
        userStr,
        f.userId?._id || f.userId,
      ]
        .map((v) => String(v || "").toLowerCase());
      return words.length === 0 || words.every((w) => fields.some((field) => field.includes(w)));
    });
  }, [forms, search]);

  const workersForDropdown = useMemo(() => {
    return appUsers.slice().sort((a, b) => {
      const na = `${a.firstName || ""} ${a.lastName || ""}`.trim() || a.email || "";
      const nb = `${b.firstName || ""} ${b.lastName || ""}`.trim() || b.email || "";
      return na.localeCompare(nb);
    });
  }, [appUsers]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice().reverse().slice((page - 1) * pageSize, page * pageSize);

  const updatePlanBlock = (blockKey, key, value) => {
    setPayload((prev) => ({
      ...prev,
      [blockKey]: { ...(prev[blockKey] || {}), [key]: value },
    }));
  };

  return (
    <>
      <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0px 4px 20px rgba(0,0,0,0.08)", padding: 24, marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 16 }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: "#1a202c", margin: 0 }}>SF-008-D Fatigue Management Plan</h2>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <i className="fa fa-search" style={{ position: "absolute", left: 12, color: THEME, fontSize: 14, pointerEvents: "none", zIndex: 1 }} />
              <input
                type="text"
                placeholder="Type to Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                style={{ padding: "10px 12px 10px 36px", borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 14, outline: "none", width: 260, boxSizing: "border-box" }}
              />
            </div>
            {(currentUserRole || "").toLowerCase() === "manager" && (
              <button
                type="button"
                onClick={openAddModal}
                style={{
                  background: THEME,
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  padding: "10px 14px",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                <i className="fa fa-plus" style={{ marginRight: 8 }} />
                Add New
              </button>
            )}
          </div>
        </div>
        <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #e2e8f0" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f7fafc", borderBottom: "1px solid #e2e8f0" }}>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Employee Name</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Employee Number</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Job Role</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Updated Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Status</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ textAlign: "center", padding: 40, color: THEME, fontSize: 16, fontWeight: 600 }}><i className="fa fa-spinner fa-spin" style={{ marginRight: 8 }} />Loading records...</td></tr>
              ) : paged.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: "center", padding: 40, color: "#718096", fontSize: 16 }}>No records found.</td></tr>
              ) : (
                paged.map((r, idx) => {
                  const updatedDate = r.updatedAt || r.createdAt || r.date;
                  return (
                    <tr key={r._id || idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{r.employeeName || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{r.employeeNumber || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{r.jobRole || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{formatDate(r.date)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{formatDate(updatedDate)}</td>
                      <td style={{ padding: 16 }}>
                        {(() => {
                          const s = String(r.status || "pending").toLowerCase();
                          const map = {
                            approved: { label: "Approved", color: "#1b5e20", bg: "#e6f4ea", border: "#b7dfc2" },
                            rejected: { label: "Rejected", color: "#b91c1c", bg: "#fde8e8", border: "#f5b5b5" },
                            pending: { label: "Pending", color: "#92400e", bg: "#fef3c7", border: "#fcd9a3" },
                          };
                          const st = map[s] || map.pending;
                          return <span style={{ fontSize: 12, fontWeight: 700, color: st.color, background: st.bg, border: `1px solid ${st.border}`, borderRadius: 6, padding: "2px 10px" }}>{st.label}</span>;
                        })()}
                      </td>
                      <td style={{ padding: 16 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <button type="button" onClick={() => handleGeneratePdf(r)} title="Generate PDF" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}><i className="fa fa-file-pdf" style={{ color: THEME, fontSize: 16 }} /></button>
                          <button type="button" onClick={() => openEditModal(r)} title="Edit" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}><i className="fa fa-edit" style={{ color: "#22c55e", fontSize: 16 }} /></button>
                          <button type="button" onClick={() => { setDeleteForm(r); setShowDeleteModal(true); }} title="Delete" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}><i className="fa fa-trash" style={{ color: "#e53e3e", fontSize: 16 }} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: 18, gap: 12 }}>
          <button className="horizon-table-btn" disabled={page === 1} onClick={() => setPage(page - 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: page === 1 ? "#f7fafc" : "#fff", color: page === 1 ? "#a0aec0" : THEME, cursor: page === 1 ? "not-allowed" : "pointer" }}>Prev</button>
          <span style={{ fontWeight: 700, color: THEME, fontSize: 16 }}>{page} / {totalPages}</span>
          <button className="horizon-table-btn" disabled={page === totalPages} onClick={() => setPage(page + 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: page === totalPages ? "#f7fafc" : "#fff", color: page === totalPages ? "#a0aec0" : THEME, cursor: page === totalPages ? "not-allowed" : "pointer" }}>Next</button>
        </div>
      </div>

      {showModal && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{ maxWidth: "1200px", margin: "0 auto", borderRadius: "16px", boxShadow: "0 10px 40px rgba(0,0,0,0.2)" }}>
              <div className="modal-header" style={{ borderBottom: "2px solid #f0f0f0", padding: "16px 20px", background: "linear-gradient(135deg, #8b1634 0%, #a01a3f 100%)" }}>
                <h5 className="modal-title" style={{ color: "#fff", fontWeight: 600, fontSize: "18px" }}>
                  {editingForm ? "Edit SF-008-D Fatigue Management Plan" : "Add SF-008-D Fatigue Management Plan"}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => {
                    setShowModal(false);
                    setWorkerRequiredError(false);
                  }}
                />
              </div>
              <div className="modal-body" style={{ padding: "16px 18px 24px", background: "#fff" }}>
                <div style={{ background: "#fff", border: `1px solid ${WORD_BORDER}`, padding: "16px 18px 20px", maxWidth: "100%" }}>
                  <div
                    style={{
                      marginBottom: 12,
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "flex-start",
                      gap: "6px 10px",
                      padding: "6px 8px",
                      borderRadius: 6,
                      background: "#fafafa",
                      border: `1px solid rgba(139, 22, 52, 0.18)`,
                      boxShadow: "0 1px 0 rgba(255,255,255,0.9) inset",
                    }}
                  >
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 22,
                          height: 22,
                          borderRadius: 5,
                          background: THEME,
                          color: "#fff",
                          fontSize: 10,
                        }}
                        aria-hidden
                      >
                        <i className="fa fa-user" />
                      </span>
                      <span style={{ fontSize: 10, fontWeight: 700, color: THEME, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                        Worker<span style={{ color: "#dc2626", marginLeft: 2 }}>*</span>
                      </span>
                    </span>
                    <div style={{ flex: "1 1 160px", maxWidth: 220, minWidth: 140, display: "flex", flexDirection: "column", alignSelf: "flex-start" }}>
                    <FormControl
                      size="small"
                      error={workerRequiredError}
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: 30,
                          fontSize: 11,
                          borderRadius: "5px",
                          backgroundColor: "#fff",
                        },
                        "& .MuiOutlinedInput-input": {
                          py: 0,
                          px: 0.75,
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(139, 22, 52, 0.22)",
                        },
                        "& .MuiOutlinedInput-root:hover:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
                          borderColor: THEME,
                        },
                        "& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
                          borderColor: THEME,
                          borderWidth: 1,
                        },
                        "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#dc2626",
                        },
                      }}
                    >
                      <Select
                        displayEmpty
                        value={payload.userId || ""}
                        onChange={(e) => {
                          setWorkerRequiredError(false);
                          setPayload((prev) => ({ ...prev, userId: e.target.value }));
                        }}
                        sx={{
                          "& .MuiSelect-select": {
                            display: "flex",
                            alignItems: "center",
                            minHeight: 28,
                            py: 0,
                            lineHeight: 1.3,
                          },
                          "& .MuiSvgIcon-root": {
                            color: THEME,
                            fontSize: "1rem",
                            right: 6,
                          },
                        }}
                        renderValue={(selected) => {
                          if (selected === "" || selected == null) {
                            return <span style={{ color: "#94a3b8", fontSize: 11 }}>Select worker…</span>;
                          }
                          const u = workersForDropdown.find((x) => String(x._id) === String(selected));
                          if (!u) return "";
                          return (
                            <span style={{ color: "#0f172a", fontSize: 11, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {[u.firstName, u.lastName].filter(Boolean).join(" ").trim() || u.email || ""}
                            </span>
                          );
                        }}
                        MenuProps={{
                          disableAutoFocusItem: true,
                          anchorOrigin: { vertical: "bottom", horizontal: "left" },
                          transformOrigin: { vertical: "top", horizontal: "left" },
                          PaperProps: {
                            sx: {
                              maxHeight: 200,
                              minWidth: 200,
                              mt: 0.25,
                              py: 0.25,
                              boxShadow: "0 8px 20px rgba(15, 23, 42, 0.1)",
                              border: "1px solid rgba(139, 22, 52, 0.12)",
                              borderRadius: "6px",
                              scrollbarWidth: "thin",
                              scrollbarColor: `${THEME} #f1f5f9`,
                              "&::-webkit-scrollbar": { width: 5 },
                              "&::-webkit-scrollbar-track": { background: "#f8fafc", borderRadius: 3 },
                              "&::-webkit-scrollbar-thumb": {
                                background: "rgba(139, 22, 52, 0.35)",
                                borderRadius: 3,
                              },
                              "&::-webkit-scrollbar-thumb:hover": {
                                background: "rgba(139, 22, 52, 0.55)",
                              },
                              "& .MuiMenuItem-root": {
                                fontSize: 11,
                                minHeight: 0,
                                py: 0.65,
                                px: 1.25,
                                lineHeight: 1.35,
                              },
                              "& .MuiMenuItem-root:hover": {
                                backgroundColor: "rgba(139, 22, 52, 0.07)",
                              },
                              "& .MuiMenuItem-root.Mui-selected": {
                                backgroundColor: "rgba(139, 22, 52, 0.12) !important",
                                color: "#0f172a",
                                fontWeight: 600,
                              },
                              "& .MuiMenuItem-root.Mui-selected:hover": {
                                backgroundColor: "rgba(139, 22, 52, 0.16) !important",
                              },
                              "& .MuiMenuItem-root.Mui-focusVisible": {
                                backgroundColor: "rgba(139, 22, 52, 0.1)",
                              },
                            },
                          },
                        }}
                      >
                        <MenuItem value="" sx={{ fontSize: 11, color: "#64748b" }}>
                          Select worker…
                        </MenuItem>
                        {workersForDropdown.map((u) => (
                          <MenuItem key={u._id} value={u._id} sx={{ fontSize: 11 }}>
                            {[u.firstName, u.lastName].filter(Boolean).join(" ").trim() || u.email}
                          </MenuItem>
                        ))}
                      </Select>
                      {workerRequiredError ? (
                        <FormHelperText error sx={{ mx: 0, mt: 0.25, fontSize: 10, lineHeight: 1.2 }}>
                          Worker is required
                        </FormHelperText>
                      ) : null}
                    </FormControl>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 16, paddingBottom: 12, borderBottom: `1px solid ${WORD_BORDER}` }}>
                    <div style={{ flex: 1 }} />
                    <div style={{ flex: 2, textAlign: "center" }}>
                      <div style={{ fontSize: 20, fontWeight: 700, color: WORD_TITLE, letterSpacing: 0.3, fontFamily: "Arial, Helvetica, sans-serif" }}>Fatigue Management Plan</div>
                      <div style={{ fontSize: 10, color: THEME, marginTop: 4, fontWeight: 600 }}>SF-008-D</div>
                    </div>
                    <div style={{ flex: 1, textAlign: "right" }}>
                      <img src={formLogo} alt="" style={{ maxHeight: 48, maxWidth: "100%", objectFit: "contain" }} />
                    </div>
                  </div>

                  <table style={DOC.table}>
                    <tbody>
                      <tr>
                        <th style={{ ...DOC.th, width: "25%" }}>Employee name</th>
                        <th style={{ ...DOC.th, width: "25%" }}>Employee number</th>
                        <th style={{ ...DOC.th, width: "25%" }}>Sentinel number (if applicable)</th>
                        <th style={{ ...DOC.th, width: "25%" }}>Normal Work location</th>
                      </tr>
                      <tr>
                        <td style={DOC.td}><input style={DOC.input} type="text" value={payload.employeeName || ""} onChange={(e) => setPayload((prev) => ({ ...prev, employeeName: e.target.value }))} /></td>
                        <td style={DOC.td}><input style={DOC.input} type="text" value={payload.employeeNumber || ""} onChange={(e) => setPayload((prev) => ({ ...prev, employeeNumber: e.target.value }))} /></td>
                        <td style={DOC.td}><input style={DOC.input} type="text" value={payload.sentinelNumber || ""} onChange={(e) => setPayload((prev) => ({ ...prev, sentinelNumber: e.target.value }))} /></td>
                        <td style={DOC.td}><input style={DOC.input} type="text" value={payload.normalWorkLocation || ""} onChange={(e) => setPayload((prev) => ({ ...prev, normalWorkLocation: e.target.value }))} /></td>
                      </tr>
                      <tr>
                        <th style={DOC.th}>Job role</th>
                        <th style={DOC.th}>Date</th>
                        <th style={{ ...DOC.th }} colSpan={2}>Name and role of person (or line manager) completing fatigue management plan</th>
                      </tr>
                      <tr>
                        <td style={DOC.td}><input style={DOC.input} type="text" value={payload.jobRole || ""} onChange={(e) => setPayload((prev) => ({ ...prev, jobRole: e.target.value }))} /></td>
                        <td style={DOC.td}><input style={DOC.input} type="date" value={payload.date || ""} onChange={(e) => setPayload((prev) => ({ ...prev, date: e.target.value }))} /></td>
                        <td style={DOC.td} colSpan={2}><input style={DOC.input} type="text" value={payload.managerNameAndRole || ""} onChange={(e) => setPayload((prev) => ({ ...prev, managerNameAndRole: e.target.value }))} /></td>
                      </tr>
                      <tr>
                        <th style={{ ...DOC.th }} colSpan={4}>
                          Reason for creation of fatigue management plan (trigger type). Include detail on the (potential) causes and impact of the fatigue issue identified:
                        </th>
                      </tr>
                      <tr>
                        <td style={DOC.td} colSpan={4}>
                          <textarea style={{ ...DOC.textarea, minHeight: 88 }} value={payload.reasonForCreation || ""} onChange={(e) => setPayload((prev) => ({ ...prev, reasonForCreation: e.target.value }))} />
                        </td>
                      </tr>
                      <tr>
                        <td style={{ ...DOC.sectionBar }} colSpan={4}>How alert are you feeling? (Select one)</td>
                      </tr>
                      <tr>
                        <td style={{ ...DOC.td, padding: 0 }} colSpan={4}>
                          <table style={{ width: "100%", borderCollapse: "collapse", ...DOC.table }}>
                            <tbody>
                              {PLAN_ALERT_OPTIONS.map((opt, index) => {
                                const current = String(payload.howAlertAreYouFeeling || "").trim();
                                const leftSelected = current === opt.left.value;
                                const rightSelected = opt.right && current === opt.right.value;
                                return (
                                  <tr key={opt.left.value || index}>
                                    <td style={{ ...DOC.td, width: "38%" }}>
                                      <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", margin: 0, fontSize: 10 }}>
                                        <input
                                          type="radio"
                                          name="howAlertPlanDoc"
                                          checked={leftSelected}
                                          onChange={() => setPayload((prev) => ({ ...prev, howAlertAreYouFeeling: opt.left.value }))}
                                          style={{ width: 14, height: 14, flexShrink: 0, accentColor: THEME }}
                                        />
                                        {opt.left.label}
                                      </label>
                                    </td>
                                    <td style={{ ...DOC.td, width: "12%", textAlign: "center" }} />
                                    <td style={{ ...DOC.td, width: "38%" }}>
                                      {opt.right ? (
                                        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", margin: 0, fontSize: 10 }}>
                                          <input
                                            type="radio"
                                            name="howAlertPlanDoc"
                                            checked={rightSelected}
                                            onChange={() => setPayload((prev) => ({ ...prev, howAlertAreYouFeeling: opt.right.value }))}
                                            style={{ width: 14, height: 14, flexShrink: 0, accentColor: THEME }}
                                          />
                                          {opt.right.label}
                                        </label>
                                      ) : null}
                                    </td>
                                    <td style={{ ...DOC.td, width: "12%", textAlign: "center" }} />
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                      <tr>
                        <td style={{ ...DOC.sectionBar }} colSpan={4}>
                          Work status over the course of the fatigue management plan (in work, restricted duties, restricted duties recommended, working hours or duties limitations etc.)
                        </td>
                      </tr>
                      <tr>
                        <td style={DOC.td} colSpan={4}>
                          <textarea style={{ ...DOC.textarea, minHeight: 80 }} value={payload.workStatusOverPlan || ""} onChange={(e) => setPayload((prev) => ({ ...prev, workStatusOverPlan: e.target.value }))} />
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <table style={{ ...DOC.table, marginTop: 16 }}>
                    <thead>
                      <tr>
                        <th style={{ ...DOC.thCtr, width: "100%" }} colSpan={3}>
                          Fatigue Management Plan
                        </th>
                      </tr>
                      <tr>
                        <th style={{ ...DOC.th, width: "35%" }}>Risk area and discussion points</th>
                        <th style={{ ...DOC.th, width: "50%" }}>Agreed action (including accountabilities and responsibilities)</th>
                        <th style={{ ...DOC.th, width: "15%", textAlign: "center" }}>Review period/date</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={DOC.tdGrey}>
                          <div style={{ fontWeight: 700, marginBottom: 6 }}>Existing work patterns:</div>
                          <div>• Discuss working hours and patterns leading to fatigue management plan</div>
                          <div>• Identify any health related or family/home issues that may be considered.</div>
                        </td>
                        <td style={DOC.td}><textarea style={DOC.textarea} rows={5} value={payload.existingWorkPatterns?.agreedActions || ""} onChange={(e) => updatePlanBlock("existingWorkPatterns", "agreedActions", e.target.value)} /></td>
                        <td style={{ ...DOC.td, textAlign: "center", verticalAlign: "middle" }}>
                          <DatePicker
                            selected={payload.existingWorkPatterns?.reviewPeriodDate ? parseYYYYMMDDLocal(getDateInputValue(payload.existingWorkPatterns.reviewPeriodDate)) : null}
                            onChange={(d) => updatePlanBlock("existingWorkPatterns", "reviewPeriodDate", d ? formatDateYYYYMMDDLocal(d) : "")}
                            dateFormat="dd/MM/yyyy"
                            className="form-control form-control-sm"
                            wrapperClassName="w-100"
                            placeholderText="dd/mm/yyyy"
                            popperProps={{ style: { zIndex: 1060 } }}
                          />
                        </td>
                      </tr>
                      <tr>
                        <td style={DOC.tdGrey}>
                          <div style={{ fontWeight: 700, marginBottom: 6 }}>Review of control measures/interventions:</div>
                          <div>• Discussion about working practices and working hours limits to reduce risk and identify controls to be implemented.</div>
                        </td>
                        <td style={DOC.td}><textarea style={DOC.textarea} rows={5} value={payload.reviewOfControlMeasures?.agreedActions || ""} onChange={(e) => updatePlanBlock("reviewOfControlMeasures", "agreedActions", e.target.value)} /></td>
                        <td style={{ ...DOC.td, textAlign: "center", verticalAlign: "middle" }}>
                          <DatePicker
                            selected={payload.reviewOfControlMeasures?.reviewPeriodDate ? parseYYYYMMDDLocal(getDateInputValue(payload.reviewOfControlMeasures.reviewPeriodDate)) : null}
                            onChange={(d) => updatePlanBlock("reviewOfControlMeasures", "reviewPeriodDate", d ? formatDateYYYYMMDDLocal(d) : "")}
                            dateFormat="dd/MM/yyyy"
                            className="form-control form-control-sm"
                            wrapperClassName="w-100"
                            placeholderText="dd/mm/yyyy"
                            popperProps={{ style: { zIndex: 1060 } }}
                          />
                        </td>
                      </tr>
                      <tr>
                        <td style={DOC.tdGrey}>
                          <div style={{ fontWeight: 700, marginBottom: 6 }}>Medical review plans:</div>
                          <div>• Discuss any OH recommendations relating to employee (returning to work after absence etc.).</div>
                        </td>
                        <td style={DOC.td}><textarea style={DOC.textarea} rows={5} value={payload.medicalReviewPlans?.agreedActions || ""} onChange={(e) => updatePlanBlock("medicalReviewPlans", "agreedActions", e.target.value)} /></td>
                        <td style={{ ...DOC.td, textAlign: "center", verticalAlign: "middle" }}>
                          <DatePicker
                            selected={payload.medicalReviewPlans?.reviewPeriodDate ? parseYYYYMMDDLocal(getDateInputValue(payload.medicalReviewPlans.reviewPeriodDate)) : null}
                            onChange={(d) => updatePlanBlock("medicalReviewPlans", "reviewPeriodDate", d ? formatDateYYYYMMDDLocal(d) : "")}
                            dateFormat="dd/MM/yyyy"
                            className="form-control form-control-sm"
                            wrapperClassName="w-100"
                            placeholderText="dd/mm/yyyy"
                            popperProps={{ style: { zIndex: 1060 } }}
                          />
                        </td>
                      </tr>
                      <tr>
                        <td style={DOC.tdGrey}>
                          <div style={{ fontWeight: 700, marginBottom: 6 }}>Any other comments:</div>
                          <div style={{ fontStyle: "italic" }}>Note: include detail of any other occupational health management action plan</div>
                        </td>
                        <td style={DOC.td}><textarea style={DOC.textarea} rows={5} value={payload.otherComments?.agreedActions || ""} onChange={(e) => updatePlanBlock("otherComments", "agreedActions", e.target.value)} /></td>
                        <td style={{ ...DOC.td, fontSize: 9, lineHeight: 1.45, verticalAlign: "bottom", fontStyle: "italic", color: "#000" }}>
                          Note: not more than 12 months from the creation of the plan
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <table style={{ ...DOC.table, marginTop: 16 }}>
                    <tbody>
                      <tr>
                        <td style={{ ...DOC.td, borderBottom: `1px solid ${WORD_BORDER}` }} colSpan={4}>
                          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, fontSize: 11 }}>
                            <span>
                              The above action plan has been discussed and agreed. Review dates will be as indicated.{" "}
                              <strong>Action plan review date:</strong>
                            </span>
                            <div style={{ minWidth: 160 }}>
                              <DatePicker
                                selected={payload.otherComments?.reviewPeriodDate ? parseYYYYMMDDLocal(getDateInputValue(payload.otherComments.reviewPeriodDate)) : null}
                                onChange={(d) => updatePlanBlock("otherComments", "reviewPeriodDate", d ? formatDateYYYYMMDDLocal(d) : "")}
                                dateFormat="dd/MM/yyyy"
                                className="form-control form-control-sm"
                                wrapperClassName="w-100"
                                placeholderText="dd/mm/yyyy"
                                popperProps={{ style: { zIndex: 1060 } }}
                              />
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <th style={{ ...DOC.th, width: "18%", fontSize: 12, color: THEME }}>Signature of employee</th>
                        <td style={{ ...DOC.td, width: "32%", verticalAlign: "top", minHeight: 210 }}>
                          <FatigueSignaturePad
                            canvasRef={employeeSigRef}
                            onEnd={saveEmployeeSignatureToPayload}
                            onClear={clearEmployeeSignature}
                            isOpen={showModal}
                          />
                        </td>
                        <th style={{ ...DOC.th, width: "18%", fontSize: 12, color: THEME }}>Signature of person completing fatigue management plan</th>
                        <td style={{ ...DOC.td, width: "32%", verticalAlign: "top", minHeight: 210 }}>
                          <FatigueSignaturePad
                            canvasRef={managerSigRef}
                            onEnd={saveManagerSignatureToPayload}
                            onClear={clearManagerSignature}
                            isOpen={showModal}
                          />
                        </td>
                      </tr>
                      <tr>
                        <th style={DOC.th}>Date</th>
                        <td style={DOC.td}>
                          <DatePicker
                            selected={payload.actionPlanAgreement?.employeeDate ? parseYYYYMMDDLocal(getDateInputValue(payload.actionPlanAgreement.employeeDate)) : null}
                            onChange={(d) => updatePlanBlock("actionPlanAgreement", "employeeDate", d ? formatDateYYYYMMDDLocal(d) : "")}
                            dateFormat="dd/MM/yyyy"
                            className="form-control form-control-sm"
                            wrapperClassName="w-100"
                            placeholderText="dd/mm/yyyy"
                            popperProps={{ style: { zIndex: 1060 } }}
                          />
                        </td>
                        <th style={DOC.th}>Date</th>
                        <td style={DOC.td}>
                          <DatePicker
                            selected={payload.actionPlanAgreement?.managerDate ? parseYYYYMMDDLocal(getDateInputValue(payload.actionPlanAgreement.managerDate)) : null}
                            onChange={(d) => updatePlanBlock("actionPlanAgreement", "managerDate", d ? formatDateYYYYMMDDLocal(d) : "")}
                            dateFormat="dd/MM/yyyy"
                            className="form-control form-control-sm"
                            wrapperClassName="w-100"
                            placeholderText="dd/mm/yyyy"
                            popperProps={{ style: { zIndex: 1060 } }}
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <div
                    style={{
                      marginTop: 14,
                      padding: 12,
                      border: `1px solid ${WORD_BORDER}`,
                      background: "#fff",
                      fontSize: 10,
                      lineHeight: 1.5,
                      color: "#1a1a1a",
                    }}
                  >
                    <p style={{ margin: "0 0 10px 0" }}>
                      <strong style={{ color: "#000" }}>
                        This document forms a record of discussions that have taken place, together with control measures implemented by the company as a safeguard regarding the individual employee&apos;s health and wellbeing in relation to fatigue. It should be signed by the line manager and employee, with copies maintained for records and reviewed at agreed dates.
                      </strong>
                    </p>
                    <p style={{ margin: 0 }}>
                      This plan must be retained with the employees&apos; employment records for a minimum of three years.
                    </p>
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: "2px solid #f0f0f0", padding: "16px 20px" }}>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setShowModal(false);
                    setWorkerRequiredError(false);
                  }}
                  style={{ borderRadius: "8px", padding: "8px 20px" }}
                >
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ background: THEME, borderColor: THEME, borderRadius: "8px", padding: "8px 20px" }}>
                  {saving ? (editingForm ? "Updating..." : "Creating...") : editingForm ? "Update" : "Create"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && deleteForm && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header" style={{ background: "#fee2e2" }}>
                <h5 className="modal-title" style={{ color: "#991b1b" }}>Delete Plan</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
              </div>
              <div className="modal-body">
                <p style={{ marginBottom: 0 }}>
                  Are you sure you want to delete <strong>{deleteForm.id || "this record"}</strong>?
                </p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                <button type="button" className="btn btn-danger" onClick={handleDeleteConfirm}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default DFatigueManagementPlanPage;
